import { CdkDragDrop, DragDropModule } from '@angular/cdk/drag-drop';
import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  linkedSignal,
  model,
  output,
  signal,
  Signal,
  untracked,
  WritableSignal
} from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AuthFacade } from '@iot-platform/auth';
import { FavoriteViewsService } from '@iot-platform/data-access/favorite-views';
import {
  CardLoaderModule,
  DragDropContentComponent,
  DragDropElementComponent,
  DragDropIndicatorComponent,
  DragDropMetaComponent
} from '@iot-platform/iot-platform-ui';
import { BaseUser, BusinessProfile, FavoriteView } from '@iot-platform/models/common';
import { TranslatePipe } from '@ngx-translate/core';
import { finalize, of, switchMap } from 'rxjs';

@Component({
  selector: 'iot-platform-ui-favorite-view-subscribers-form',
  imports: [
    FormsModule,
    TranslatePipe,
    MatIconModule,
    MatTooltipModule,
    NgTemplateOutlet,
    CardLoaderModule,
    DragDropModule,
    DragDropElementComponent,
    DragDropIndicatorComponent,
    DragDropContentComponent,
    DragDropMetaComponent
  ],
  templateUrl: './subscribers-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FavoriteViewSubscribersFormComponent {
  favoriteView = input<FavoriteView>();
  businessProfile = input<BusinessProfile>();
  isShared = input<boolean>(false);
  isActive = input<boolean>(false);
  formValue = output<{ subscribers: string[] | null }>();
  formStateChanged = output<boolean>();
  usersLoading: WritableSignal<boolean> = signal(false);
  search = model<string>('');
  selectedSubscriberIds: WritableSignal<string[]> = linkedSignal<FavoriteView | undefined, string[]>({
    source: this.favoriteView,
    computation: (favoriteView) => favoriteView?.export?.subscribers?.map((subscriber) => subscriber.id) ?? []
  });
  formValid: Signal<boolean> = computed(() => !this.isActive() || !!this.selectedUsers().length);
  emitEffect = effect(() => {
    const selectedIds = this.selectedSubscriberIds();
    const subscribers: string[] = this.selectedUsers()?.map((user) => user.id);

    const initialIds = untracked(() => new Set(this.favoriteView()?.export?.subscribers?.map((subscriber) => subscriber.id) ?? []));
    const changed = selectedIds.length !== initialIds.size || selectedIds.some((id) => !initialIds.has(id));

    this.formStateChanged.emit(changed);

    if (!this.formValid()) {
      this.formValue.emit(null);
      return;
    }

    this.formValue.emit({ subscribers });
  });
  private readonly favoriteViewsService: FavoriteViewsService = inject(FavoriteViewsService);
  private readonly authFacade: AuthFacade = inject(AuthFacade);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  users: Signal<BaseUser[]> = toSignal(
    toObservable(computed(() => ({ isShared: this.isShared(), businessProfile: this.businessProfile() }))).pipe(
      switchMap(({ isShared, businessProfile }) => {
        const businessProfileId = businessProfile?.id;
        if (!isShared) {
          this.usersLoading.set(false);
          const { id, email, firstname, lastname } = this.authFacade.currentUser();
          return of([{ id, email, firstname, lastname } as BaseUser]);
        }
        if (!businessProfileId) {
          this.usersLoading.set(false);
          return of([]);
        }
        this.usersLoading.set(true);
        return this.favoriteViewsService.getBusinessProfileUsers(businessProfileId).pipe(finalize(() => this.usersLoading.set(false)));
      }),
      takeUntilDestroyed(this.destroyRef)
    ),
    { initialValue: [] }
  );
  selectedUsers: Signal<BaseUser[]> = computed(() => {
    const usersById = new Map(this.users().map((user) => [user.id, user]));
    return this.selectedSubscriberIds()
      .map((id) => usersById.get(id))
      .filter((user): user is BaseUser => !!user);
  });
  availableUsers: Signal<BaseUser[]> = computed(() => {
    const selected = new Set(this.selectedSubscriberIds());
    const term = this.search().trim().toLowerCase();
    return this.users()
      .filter((user) => !selected.has(user.id))
      .filter((user) => !term || user.firstname?.toLowerCase().includes(term) || user.lastname?.toLowerCase().includes(term));
  });

  onAdd(user: BaseUser): void {
    const id = user.id;
    if (!id) {
      return;
    }
    this.selectedSubscriberIds.update((ids) => (ids.includes(id) ? ids : [...ids, id]));
  }

  onRemove(user: BaseUser): void {
    this.selectedSubscriberIds.update((ids) => ids.filter((id) => id !== user.id));
  }

  onDropToSelected(event: CdkDragDrop<BaseUser[]>): void {
    if (event.previousContainer === event.container) {
      return;
    }
    this.onAdd(event.item.data as BaseUser);
  }

  onDropToAvailable(event: CdkDragDrop<BaseUser[]>): void {
    if (event.previousContainer === event.container) {
      return;
    }
    this.onRemove(event.item.data as BaseUser);
  }
}
